import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ChevronRight } from "lucide-react";
import { api, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { statusLabel } from "@/lib/status";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";

export default function OrdersPage() {
  useSeo({ title: "My Orders | Soonyoung", noindex: true, path: "/orders" });
  const { isAuthed, setLoginOpen, authReady } = useStore();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (isAuthed) api.get("/ecom/orders?page_size=50").then((r) => setOrders(r.data?.items || r.data || [])).catch(() => setOrders([]));
  }, [isAuthed]);

  if (authReady && !isAuthed) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-center justify-center px-4 pt-28 text-center" data-testid="orders-login-prompt">
        <Package className="h-10 w-10 text-gold" />
        <h1 className="mt-4 font-display text-3xl text-cream">Sign in to see your orders</h1>
        <button data-testid="orders-login-btn" onClick={() => setLoginOpen(true)} className="mt-6 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest">Sign in with OTP</button>
      </div>
    );
  }

  return (
    <section data-testid="orders-page" className="mx-auto max-w-5xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32">
      <Reveal>
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">Your History</p>
        <h1 className="mt-2 font-display text-3xl tracking-tight text-cream sm:text-5xl">My Orders</h1>
      </Reveal>

      {!orders ? (
        <div className="mt-10 space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-28 animate-pulse rounded-3xl bg-moss" />)}</div>
      ) : orders.length === 0 ? (
        <div className="mt-12 flex flex-col items-center rounded-3xl border border-dashed border-champagne/60 py-24 text-center" data-testid="orders-empty">
          <p className="font-display text-2xl text-cream">No orders yet</p>
          <p className="mt-2 text-sm text-sage">Your beauty history begins with the first ritual.</p>
          <Link to="/shop" className="mt-6 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest">Start Shopping</Link>
        </div>
      ) : (
        <ul className="mt-10 space-y-4">
          {orders.map((o) => (
            <li key={o.id}>
              <Link
                to={`/orders/${o.id}`}
                data-testid={`order-row-${o.id}`}
                className="flex items-center justify-between gap-4 rounded-3xl border border-champagne/40 bg-pine p-5 transition-colors hover:border-gold/50 sm:p-6"
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fern">{o.order_number}</p>
                  <p className="mt-1 font-display text-lg text-cream">{inr(o.grand_total)} · {o.items?.length || 0} items</p>
                  <p className="mt-1 text-xs text-fern">{o.created_at ? new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`rounded-full px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] ${["delivered", "paid"].includes(o.status) ? "bg-moss text-cream" : ["cancelled", "payment_failed"].includes(o.status) ? "bg-gold/15 text-gold" : "bg-champagne/30 text-cream"}`} data-testid={`order-status-${o.id}`}>
                    {statusLabel(o.status)}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gold" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
